import {
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { createLayoutResizeObserver } from '@features/shared/utils/layoutResizeObserver'
import {
  MENU_SCROLL_THUMB_INSET_PX,
  menuScrollPerThumbPixel,
  menuScrollThumb,
} from '../logic/menuScrollThumb'

interface MenuScrollAreaProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  /** How the area sizes in its column (`flex-1`, or `flex-auto` to fit its rows). */
  className?: string
  children: ReactNode
}

/**
 * A sidebar menu's scrolling list, padded by the rail's inset all round. While
 * it holds more rows than fit, a scroll lane runs down its right side behind a
 * muted line, with the list's own thumb in it (menu-scroll-lane): inset 1px
 * from the lane's edges all round, rounded at both ends, darker when hovered
 * or dragged. The line
 * above the list (the menu's title band, or the one under its search) is the
 * lane's top, and the footer's line or the card's edge its bottom. A list that
 * fits has no lane. The wheel, trackpad and keys scroll the list as usual, and
 * dragging the thumb scrolls it too; the browser's scrollbar is hidden, so the
 * lane looks the same in every browser.
 *
 * The props go to the scrolling element (the list's viewport).
 */
export default function MenuScrollArea({
  className = '',
  children,
  ...viewportProps
}: MenuScrollAreaProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  // Whether the rows overflow: the lane, and the room for it, only then.
  const [scrollable, setScrollable] = useState(false)
  const scrollableRef = useRef(false)

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const content = contentRef.current
    const thumb = thumbRef.current
    if (!viewport || !content || !thumb) return

    // Written straight to the thumb, so scrolling never re-renders the list;
    // only the list starting or stopping to overflow does.
    const update = () => {
      const place = menuScrollThumb(
        viewport.scrollTop,
        viewport.scrollHeight,
        viewport.clientHeight,
        viewport.clientHeight - MENU_SCROLL_THUMB_INSET_PX * 2
      )
      if (scrollableRef.current !== (place !== null)) {
        scrollableRef.current = place !== null
        setScrollable(place !== null)
      }
      if (!place) return
      thumb.style.height = `${place.length}px`
      thumb.style.transform = `translateY(${place.offset}px)`
    }

    update()
    viewport.addEventListener('scroll', update, { passive: true })
    // The list's height and its rows' (a folder opening, a search) move the thumb.
    const observer = createLayoutResizeObserver(update)
    observer.observe(viewport)
    observer.observe(content)
    return () => {
      viewport.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [])

  const handleThumbPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current
    const thumb = thumbRef.current
    if (event.button !== 0 || !viewport || !thumb) return
    event.preventDefault()
    event.stopPropagation()

    const startY = event.clientY
    const startScrollTop = viewport.scrollTop
    const perPixel = menuScrollPerThumbPixel(
      viewport.scrollHeight,
      viewport.clientHeight,
      thumb.offsetHeight,
      viewport.clientHeight - MENU_SCROLL_THUMB_INSET_PX * 2
    )
    thumb.setPointerCapture(event.pointerId)
    thumb.dataset.dragging = ''

    const handleMove = (moveEvent: PointerEvent) => {
      viewport.scrollTop = startScrollTop + (moveEvent.clientY - startY) * perPixel
    }
    const handleEnd = () => {
      delete thumb.dataset.dragging
      thumb.removeEventListener('pointermove', handleMove)
      thumb.removeEventListener('pointerup', handleEnd)
      thumb.removeEventListener('pointercancel', handleEnd)
    }
    thumb.addEventListener('pointermove', handleMove)
    thumb.addEventListener('pointerup', handleEnd)
    thumb.addEventListener('pointercancel', handleEnd)
  }

  return (
    <div className={`menu-scroll-area relative flex min-h-0 flex-col ${className}`}>
      <div
        ref={viewportRef}
        {...viewportProps}
        className={`menu-scroll-viewport min-h-0 flex-auto overflow-y-auto overflow-x-hidden py-[var(--sidebar-item-inset)] pl-[var(--sidebar-item-inset)] ${
          // The rows keep the inset from the lane's line, as from the card's edge.
          scrollable
            ? 'pr-[calc(var(--sidebar-item-inset)+var(--sidebar-menu-scroll-lane)+1px)]'
            : 'pr-[var(--sidebar-item-inset)]'
        }`}
      >
        <div ref={contentRef}>{children}</div>
      </div>
      <div aria-hidden="true" className="menu-scroll-lane" hidden={!scrollable}>
        <div ref={thumbRef} className="menu-scroll-thumb" onPointerDown={handleThumbPointerDown} />
      </div>
    </div>
  )
}
